import { installLiveStudyExperience } from '@/pages/try/studyExperience/liveSession';
import { installStudyStudentExperience } from '@/pages/try/studyExperience/install';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { createApplicationRenderer, type ApplicationMountState } from '@/app/applicationMount';
import { RouterProvider } from 'react-router-dom';

import { router } from '@/app/router';
import { startPageTitleTracking } from '@/app/pageTitle';
import { useBootstrap } from '@/auth/useBootstrap';
import { startPageTracking } from '@/lib/analytics';
import { installStoryBlocksQaSession } from '@/pages/learn/blocks/story-parts/storyBlocksQaSession';
import './styles/index.css';

import { installStaleChunkRecovery } from '@/lib/staleChunkRecovery';
import { installStagingBanner } from '@/stagingBanner';

// Recover from the stale-chunk deploy race BEFORE anything can lazy-import.
installStaleChunkRecovery();
installStagingBanner();
installStoryBlocksQaSession();

// GA4 page_view reporting for the PARENT PORTAL ONLY — every hit is gated on
// isPortalSurface(), so kid routes (/learn, /try, /play) never load gtag.js and
// never send anything. Also inert without parent consent, outside production,
// and under headless automation. See analytics.ts.
// Register title updates before analytics so each page_view reads the title for
// the destination route rather than the previous page's title.
startPageTitleTracking(router);
startPageTracking(router);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// Entry-point module — not a Fast Refresh target.
// eslint-disable-next-line react-refresh/only-export-components
function AppRoot() {
  useBootstrap();
  return (
    <React.Suspense
      fallback={
        <div
          role="status"
          className="grid min-h-dvh place-items-center bg-canvas p-6 text-base font-semibold text-ink"
        >
          Opening your page…
        </div>
      }
    >
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </React.Suspense>
  );
}

// A dependency update can reevaluate this entry. Keep the root in Vite's document-local
// lifecycle data so asynchronous bootstrap cannot append a second complete application.
// Success and error paths use this same renderer. Older bootstrap completions are ignored.
const mountState: ApplicationMountState = import.meta.hot?.data.applicationMount ?? {};
if (import.meta.hot) import.meta.hot.data.applicationMount = mountState;
const renderApplication = createApplicationRenderer(document.getElementById('root')!, mountState);

async function mount() {
  try {
    if (!(await installLiveStudyExperience(queryClient)))
      installStudyStudentExperience(queryClient);
    if (
      window.location.pathname === '/try/study/student' &&
      new URLSearchParams(window.location.search).get('experience') === 'live'
    )
      await router.navigate(
        window.location.hash.startsWith('#/learn/study')
          ? window.location.hash.slice(1)
          : '/learn/study',
        { replace: true },
      );
  } catch (error) {
    renderApplication(
      <main className="mx-auto max-w-2xl p-8 text-ink">
        <h1 className="text-2xl font-extrabold">Open your student experience</h1>
        <p role="alert" className="my-4">
          {error instanceof Error ? error.message : 'The experience could not be opened.'}
        </p>
        <a
          className="btn-pill-primary"
          href={`${import.meta.env.VITE_INSTITUTION_PORTAL_URL ?? 'https://institution.airbotix.ai'}/study/explore`}
        >
          Back to teaching
        </a>
      </main>,
    );
    return;
  }
  renderApplication(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <AppRoot />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}
void mount();
